import {
  deleteMilestone,
  flattenMilestones,
  forceUpdateMilestonesOnPrepopulationChange,
  forceUpdateMilestonesOnTargetDatesChange,
  mapProjectRole,
} from "@/helpers";
import {
  useGetProjectRolesByIdsMutation,
  useGetProjectTemplateQuery,
  useSaveProjectTemplateMutation,
} from "@/services";
import _ from "lodash";
import { node } from "prop-types";
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

export const ProjectTemplateContext = createContext();
export const useProjectTemplateContext = () =>
  useContext(ProjectTemplateContext);

export const ProjectTemplateProvider = ({ children }) => {
  const { id } = useParams();
  const intl = useIntl();

  const [milestoneSortData, setMilestoneSortData] = useState([]);
  const [
    shouldCompleteProjectMilestoneTemplate,
    setShouldCompleteProjectMilestoneTemplate,
  ] = useState({
    current: undefined,
    new: undefined,
  });
  const [addMilestoneModal, setAddMilestoneModal] = useState({
    open: false,
    parent: null,
  });
  const [editMilestoneModal, setEditMilestoneModal] = useState({
    open: false,
    milestoneId: null,
    parent: null,
  });
  const [milestoneListCollapsed, setMilestoneListCollapsed] = useReducer(
    (state) => !state,
    true
  );
  const [milestoneListHideDisabled, setMilestoneListHideDisabled] = useReducer(
    (state) => !state,
    false
  );
  const [projectRoles, setProjectRoles] = useState();
  const [milestoneTargetDatesUpdated, setMilestoneTargetDatesUpdated] =
    useState();

  const [milestonePrepopulationUpdated, setMilestonePrepopulationUpdated] =
    useState();

  const [
    getProjectRolesByIds,
    { data: projectRolesByIds, isLoading: loadingProjectRoles },
  ] = useGetProjectRolesByIdsMutation();

  const [saveProjectTemplate, { isLoading: savingProjectTemplate }] =
    useSaveProjectTemplateMutation();
  const { data: projectTemplate, refetch: refetchProjectTemplate } =
    useGetProjectTemplateQuery(id);

  // This useEffect and its dependencies should be removed after the template feature flag is removed
  useEffect(() => {
    if (!projectRolesByIds) return;
    const result = projectRolesByIds.map((role) => mapProjectRole(role));

    setProjectRoles(_.keyBy(result, ({ id }) => id));
  }, [projectRolesByIds]);

  useEffect(() => {
    if (!projectTemplate?.milestones) return;

    const flattenedMilestones = flattenMilestones(projectTemplate.milestones);

    // This code until next comment can be removed once the template feature flag is removed
    const approverRoleIds = flattenedMilestones
      ?.map(({ approvers }) => approvers?.map(({ roleId }) => roleId))
      .flat();
    const notificationRoleIds = flattenedMilestones
      ?.map(({ notification: { roles } = {} }) =>
        roles?.map(({ roleId }) => roleId)
      )
      .flat();
    const ids = [
      ...new Set([...approverRoleIds, ...notificationRoleIds]),
    ].filter((role) => !!role);

    if (ids.length) {
      getProjectRolesByIds(ids);
    }
    // remove up to here later

    setShouldCompleteProjectMilestoneTemplate((value) => ({
      ...value,
      current: flattenedMilestones.find(
        (milestone) => milestone.shouldCompleteProject
      ),
    }));
  }, [getProjectRolesByIds, projectTemplate?.milestones]);

  const save = (update) => {
    var updatedTemplate = forceUpdateMilestonesOnTargetDatesChange(
      milestoneTargetDatesUpdated,
      update
    );
    updatedTemplate = forceUpdateMilestonesOnPrepopulationChange(
      milestonePrepopulationUpdated,
      update
    );
    saveProjectTemplate({
      id: updatedTemplate.id,
      projectTemplate: updatedTemplate,
    })
      .unwrap()
      .then(({ milestones }) => {
        toast.success(
          intl.formatMessage({ id: `editProjectTemplate.saveSuccess` })
        );
        setMilestoneTargetDatesUpdated(null);
        setAddMilestoneModal({ open: false, parent: null });
        setEditMilestoneModal({
          open: false,
          milestoneId: null,
          parent: null,
        });
        setShouldCompleteProjectMilestoneTemplate((value) => {
          const projectMilestoneTemplateIsNewlyCreated = !!value.new?.id;

          return {
            current: projectMilestoneTemplateIsNewlyCreated
              ? value.new
              : flattenMilestones(milestones).find(
                  ({ shouldCompleteProject }) => shouldCompleteProject
                ),
            new: undefined,
          };
        });
        refetchProjectTemplate();
      })
      .catch(
        ({ response: { data: invalidProjectMilestoneTemplates } = {} }) => {
          toast.error(
            intl.formatMessage({
              id: invalidProjectMilestoneTemplates?.length
                ? "editProjectTemplate.saveErrorMilestoneCompletionData"
                : "editProjectTemplate.saveError",
            })
          );
        }
      );
  };

  const handleDeleteMilestone = (milestoneId) => {
    const updatedProjectTemplate = {
      ...projectTemplate,
      milestones: deleteMilestone(projectTemplate.milestones, milestoneId),
    };

    saveProjectTemplate({ id, projectTemplate: updatedProjectTemplate });
    refetchProjectTemplate();
  };

  const handleSortMilestones = (milestones) => {
    const updatedProjectTemplate = {
      ...projectTemplate,
      milestones: [...milestones],
    };

    saveProjectTemplate({ id, projectTemplate: updatedProjectTemplate });
    refetchProjectTemplate();
  };

  const getTargetDatesMilestoneNamesByDefaultProductCategory = (
    productCategory,
    skipMilestoneId
  ) =>
    projectTemplate?.milestones
      .filter(
        ({ targetDatesRequired, id, defaultFor }) =>
          targetDatesRequired === true &&
          id !== skipMilestoneId &&
          defaultFor?.includes(productCategory)
      )
      .map(({ name }) => name);

  const getMilestoneDatePrepopulationByDefaultProductCategory = (
    productCategory,
    skipMilestoneId,
    targetDatePrepopulationSource
  ) =>
    projectTemplate?.milestones
      .filter(
        (milestone) =>
          milestone.targetDatePrepopulationSource ===
            targetDatePrepopulationSource &&
          milestone.id !== skipMilestoneId &&
          milestone.defaultFor?.includes(productCategory)
      )
      .map(({ name }) => name);

  return (
    <ProjectTemplateContext.Provider
      value={{
        milestoneListCollapsed,
        setMilestoneListCollapsed,
        milestoneListHideDisabled,
        setMilestoneListHideDisabled,
        addMilestoneModal,
        setAddMilestoneModal,
        editMilestoneModal,
        setEditMilestoneModal,
        templateRoles: projectRoles,
        loadingTemplateRoles: loadingProjectRoles,
        handleDeleteMilestone,
        saveProjectTemplate: save,
        savingProjectTemplate,
        handleSortMilestones,
        milestoneSortData,
        setMilestoneSortData,
        shouldCompleteProjectMilestoneTemplate,
        setShouldCompleteProjectMilestoneTemplate,
        getTargetDatesMilestoneNamesByDefaultProductCategory,
        setMilestoneTargetDatesUpdated,
        projectTemplate,
        setMilestonePrepopulationUpdated,
        getMilestoneDatePrepopulationByDefaultProductCategory,
      }}
    >
      {children}
    </ProjectTemplateContext.Provider>
  );
};

ProjectTemplateProvider.propTypes = {
  children: node.isRequired,
};
